import React, { useState, useRef, useEffect } from "react";
import { roleAccess } from "helpers/RolesPermission";
import { BiPlus, BiPrinter } from "react-icons/bi";
import { HiOutlineEye } from "react-icons/hi";
import { PARENT_FORM_CATEGORY_SLUG, USER_ROLE } from "Constants/constant";
import FundingSetup from "pages/SupportCoordination/Funding/FundingSetup/index";
import DirectOpenFirstForm from "components/DynamicForms/DirectOpenFirstForm/DirectOpenFirstForm";
import Appointment from "components/ServiceProvider/Participant/Appointments/index";
import ParticipantImageToolTip from "components/common/Tooltip/ParticipantImageToolTip";
import CustomButton from "components/common/Button/CustomButton";
import CategoryFormsListing from "components/Modals/CategoryFormsListing/CategoryFormsListing";
import NotificationDropDown from "components/common/NotificationDropDown/NotificationDropDown";
import ViewMedicationPlanModal from "./ViewMedicationPlanModal";
import {
  BLK_PORTAL_PREMISSION_KEYS_CHILDREN,
  PERMISSION_KEYS,
  PERMISSION_KEYS_CHILDREN,
} from "store/constants";
import useCareLabels from "hooks/useCareLabels";

const ParticipantProfileHeader = (props) => {
  const { participantLabel } = useCareLabels();
  const { selectedTab, activeTab, participantInfo, handleAddCaseNote } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleLabelsCount, setVisibleLabelsCount] = useState(0);
  const [showMoreLabels, setShowMoreLabels] = useState(false);

  const nameContainerRef = useRef(null);
  const labelContainerRef = useRef(null);
  const buttonsContainerRef = useRef(null);

  useEffect(() => {
    const calculateVisibleLabels = () => {
      if (
        nameContainerRef.current &&
        labelContainerRef.current &&
        buttonsContainerRef.current
      ) {
        const totalWidth = nameContainerRef.current.offsetWidth;
        const buttonsWidth = buttonsContainerRef.current.offsetWidth;
        const nameWidth =
          nameContainerRef.current.querySelector("p")?.offsetWidth || 0;
        const availableWidth = totalWidth - nameWidth - buttonsWidth - 48; // Add margin

        const labels = participantInfo?.labels || [];
        let totalLabelWidth = 0;
        let count = 0;

        for (let i = 0; i < labels.length; i++) {
          totalLabelWidth += 100; // Approximate label width
          if (totalLabelWidth < availableWidth) {
            count++;
          } else {
            break;
          }
        }

        setVisibleLabelsCount(count);
      }
    };

    calculateVisibleLabels();

    const resizeObserver = new ResizeObserver(calculateVisibleLabels);
    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, [participantInfo]);

  const renderLabels = (labels) => {
    const visibleLabels = labels.slice(0, 2); // Always show the first two labels
    const hiddenLabels = labels.slice(2); // Move the rest to the dropdown

    return (
      <div
        className='relative flex items-center flex-wrap gap-2'
        ref={labelContainerRef}
      >
        {visibleLabels.map((label, index) => (
          <span
            key={label.id || index}
            className='bg-blue-dark text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm hover:shadow-md transition-shadow'
          >
            {label.label}
          </span>
        ))}
        {hiddenLabels.length > 0 && (
          <div className='relative group'>
            <span className='bg-gray-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer shadow-sm hover:shadow-md transition-all hover:bg-gray-600'>
              +{hiddenLabels.length}
            </span>
            <div className='absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl p-3 z-10 min-w-[200px] hidden group-hover:block animate-fadeIn'>
              <div className='space-y-2'>
                {hiddenLabels.map((label, index) => (
                  <span
                    key={label.id || index}
                    className='block bg-blue-50 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-full border border-blue-200'
                  >
                    {label.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCustomCrmCategoryButton = () => {
    if (!activeTab?.isCustomFormCategory || !activeTab?.categoryUuid) {
      return null;
    }
    const isFrontlineWorker = roleAccess([USER_ROLE.Sp_Standard_User]);
    return (
      <CategoryFormsListing
        {...(isFrontlineWorker ? { role: "crm" } : {})}
        participantId={participantInfo.id}
        buttonText={activeTab.name}
        slug={activeTab.slug}
        categoryUuid={activeTab.categoryUuid}
        particpantUuid={participantInfo.uuid}
      />
    );
  };

  const loadHeaderButton = () => {
    const customCategoryButton = renderCustomCrmCategoryButton();
    if (customCategoryButton) {
      return customCategoryButton;
    }

    if (roleAccess([USER_ROLE.Sp_Standard_User])) {
      switch (selectedTab) {
        case 1:
          return (
            <>
              <CustomButton
                clickHandler={() => {
                  setIsModalOpen(true);
                }}
                label='View Medication Plan'
                Icon={<HiOutlineEye />}
                style={{
                  marginRight: "16px",
                }}
              />
              <ViewMedicationPlanModal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                handleClose={() => setIsModalOpen(false)}
              />
              <div className='flex md:space-x-4 items-center text-sm print:hidden'>
                <CustomButton
                  clickHandler={() => window.print()}
                  label='Print Support Plan'
                  Icon={<BiPrinter />}
                />
              </div>
            </>
          );
        case 6:
          return (
              <>
                {
                    roleAccess(
                        [],
                        "participant",
                        BLK_PORTAL_PREMISSION_KEYS_CHILDREN.SUBMITTED.FORM.ADD
                    ) && (
                        <CategoryFormsListing
                            role = 'crm'
                            participantId={participantInfo.id}
                            buttonText={PARENT_FORM_CATEGORY_SLUG.PARTICIPANT_ASSESSMENT.NAME.replace(
                              /Participant/g,
                              participantLabel
                            )}
                            slug={PARENT_FORM_CATEGORY_SLUG.PARTICIPANT_ASSESSMENT.SLUG}
                            particpantUuid={participantInfo.uuid}
                        />
                    )
                }
              </>

          );
          case 9:
          return (
            <div className='flex space-x-3'>
              <CategoryFormsListing
                  role = 'crm'
                participantId={participantInfo.id}
                buttonText={PARENT_FORM_CATEGORY_SLUG.INJURY_MANAGEMENT.NAME}
                slug={PARENT_FORM_CATEGORY_SLUG.INJURY_MANAGEMENT.SLUG}
                particpantUuid={participantInfo.uuid}
              />
            </div>
          );
        case 5:
          return (
            <CategoryFormsListing
                role = 'crm'
              participantId={participantInfo.id}
              buttonText='Medication Plan'
              slug={PARENT_FORM_CATEGORY_SLUG.MEDICATION.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 7:
        case 11:
          return (
              <>
                {
                  roleAccess([],
                      "participant",
                      BLK_PORTAL_PREMISSION_KEYS_CHILDREN.SUBMITTED.FORM.ACCESS_RISK_MANAGEMENT_FORM
                  ) && (
                        <CategoryFormsListing
                            role = 'crm'
                            participantId={participantInfo.id}
                            buttonText={PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.NAME}
                            slug={PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.SLUG}
                            particpantUuid={participantInfo.uuid}
                        />
                    )
                }
              </>
          );
        case 8:
          return (
            <CategoryFormsListing
                role = 'crm'
              participantId={participantInfo.id}
              buttonText='High Care'
              slug={PARENT_FORM_CATEGORY_SLUG.HIGH_CARE.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 14:
          return (
            <CategoryFormsListing
              role='crm'
              participantId={participantInfo.id}
              buttonText={PARENT_FORM_CATEGORY_SLUG.PERSON_CENTERED_PLAN.NAME}
              slug={PARENT_FORM_CATEGORY_SLUG.PERSON_CENTERED_PLAN.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 15:
          return (
            <CategoryFormsListing
              role='crm'
              participantId={participantInfo.id}
              buttonText={PARENT_FORM_CATEGORY_SLUG.SUPPORT_PLAN.NAME}
              slug={PARENT_FORM_CATEGORY_SLUG.SUPPORT_PLAN.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 17:
          return roleAccess(
            [],
            PERMISSION_KEYS.PARTICIPANT,
            BLK_PORTAL_PREMISSION_KEYS_CHILDREN.PARTICIPANT.FUNDING.CREATE
          ) ? (
            <FundingSetup type='Header_button' />
          ) : null;
        case 18:
          return (
              <>
                {roleAccess([],
                    "participant",
                    BLK_PORTAL_PREMISSION_KEYS_CHILDREN.SUBMITTED.FORM.EXIT_OR_TRANSITION
                ) && (
                    <CategoryFormsListing
                        buttonText='Exit/Transition'
                        participantId={participantInfo.id}
                        slug={PARENT_FORM_CATEGORY_SLUG.Exit_Transaction.SLUG}
                        particpantUuid={participantInfo.uuid}
                    />
                )}
              </>
          );
        case 25:
          return (
            <CategoryFormsListing
              role="crm"
              participantId={participantInfo.id}
              buttonText="Other"
              slug={PARENT_FORM_CATEGORY_SLUG.Other.SLUG}
              categoryUuid={PARENT_FORM_CATEGORY_SLUG.Other.UUID}
              particpantUuid={participantInfo.uuid}
            />
          );
        default:
          return null;
      }
    } else {
      switch (selectedTab) {
        case 1:
          return (
            <div className='flex space-x-1'>
              <CustomButton
                clickHandler={() => setIsModalOpen(true)}
                label='View Medication Plan'
                Icon={<HiOutlineEye />}
                style={{ marginRight: "16px" }}
              />
              <ViewMedicationPlanModal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                handleClose={() => setIsModalOpen(false)}
              />
              <CustomButton
                clickHandler={() => window.print()}
                label='Print Support Plan'
                Icon={<BiPrinter />}
              />
            </div>
          );
        case 3:
        case 15:
          return (
            <CategoryFormsListing
              participantId={participantInfo.id}
              buttonText={PARENT_FORM_CATEGORY_SLUG.SUPPORT_PLAN.NAME}
              slug={PARENT_FORM_CATEGORY_SLUG.SUPPORT_PLAN.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 5:
          return (
            <CategoryFormsListing
              participantId={participantInfo.id}
              buttonText='Medication Plan'
              slug={PARENT_FORM_CATEGORY_SLUG.MEDICATION.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 6:
          return (
                <CategoryFormsListing
                    participantId={participantInfo.id}
                    buttonText={PARENT_FORM_CATEGORY_SLUG.PARTICIPANT_ASSESSMENT.NAME.replace(
                      /Participant/g,
                      participantLabel
                    )}
                    slug={PARENT_FORM_CATEGORY_SLUG.PARTICIPANT_ASSESSMENT.SLUG}
                    particpantUuid={participantInfo.uuid}
                />
          );
        case 7:
        case 11:
          return (
            <CategoryFormsListing
              participantId={participantInfo.id}
              buttonText={PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.NAME}
              slug={PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 14:
          return (
            <CategoryFormsListing
              participantId={participantInfo.id}
              buttonText={PARENT_FORM_CATEGORY_SLUG.PERSON_CENTERED_PLAN.NAME}
              slug={PARENT_FORM_CATEGORY_SLUG.PERSON_CENTERED_PLAN.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 8:
          return (
            <CategoryFormsListing
              participantId={participantInfo.id}
              buttonText='High Care'
              slug={PARENT_FORM_CATEGORY_SLUG.HIGH_CARE.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 9:
          return (
            <CategoryFormsListing
              participantId={participantInfo.id}
              buttonText={PARENT_FORM_CATEGORY_SLUG.INJURY_MANAGEMENT.NAME}
              slug={PARENT_FORM_CATEGORY_SLUG.INJURY_MANAGEMENT.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 12:
          return (
            <Appointment
              type='header_button'
              participantInfo={participantInfo}
            />
          );
        case 17:
          return <FundingSetup type='Header_button' />;
        case 18:
          return (
            <CategoryFormsListing
              buttonText='Exit/Transition'
              participantId={participantInfo.id}
              slug={PARENT_FORM_CATEGORY_SLUG.Exit_Transaction.SLUG}
              particpantUuid={participantInfo.uuid}
            />
          );
        case 25:
          return (
            <CategoryFormsListing
              participantId={participantInfo.id}
              buttonText="Other"
              slug={PARENT_FORM_CATEGORY_SLUG.Other.SLUG}
              categoryUuid={PARENT_FORM_CATEGORY_SLUG.Other.UUID}
              particpantUuid={participantInfo.uuid}
            />
          );
        default:
          return null;
      }
    }
  };

  const casePermissionHandle = () => {
    if (
      roleAccess(
        [USER_ROLE.Sp_Standard_User],
        PERMISSION_KEYS.PARTICIPANT,
        BLK_PORTAL_PREMISSION_KEYS_CHILDREN.PARTICIPANT.CASE_NOTES.STORE
      ) &&
      roleAccess(
        [USER_ROLE.Sp_Standard_User],
        PERMISSION_KEYS.PARTICIPANT,
        BLK_PORTAL_PREMISSION_KEYS_CHILDREN.PARTICIPANT.CASE_NOTES.SHOW
      )
    ) {
      return true;
    } else if (roleAccess([USER_ROLE.Sp_manager, USER_ROLE.Sp_Admin])) {
      return true;
    }
    return false;
  };

  return (
    <>
      {participantInfo && (
        <div
          className='px-4 sm:px-6 h-[80px] min-h-[80px] bg-white border-b border-gray-200 shadow-sm flex items-center'
          ref={nameContainerRef}
        >
          <div className='flex flex-col sm:flex-row justify-between items-center gap-3 w-full min-h-0'>
            {/* Left Section - Participant Info */}
            <div className='flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0'>
              <div className='h-12 w-12 sm:h-14 sm:w-14 flex-shrink-0 group relative'>
                <ParticipantImageToolTip
                  cssClass={"bg-gray-900 py-2 px-3 rounded-lg text-white z-[9] shadow-lg"}
                  imgCssClass={`${
                    participantInfo.profile_picture
                      ? "object-cover h-12 w-12 sm:h-14 sm:w-14 rounded-full ring-4 ring-white shadow-lg print:w-10 print:h-10"
                      : "object-cover rounded-full border-4 border-white shadow-lg"
                  }`}
                  participant={participantInfo}
                />
              </div>
              <div className='flex-1 min-w-0'>
                <div className='flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-wrap'>
                  <h1 className='capitalize text-lg sm:text-xl font-bold text-gray-900 truncate'>
                    {`${participantInfo.first_name} ${participantInfo.last_name}`}
                  </h1>
                  {participantInfo.labels &&
                    participantInfo.labels.length > 0 &&
                    renderLabels(participantInfo.labels)}
                </div>
                {roleAccess(
                  [],
                  PERMISSION_KEYS.PARTICIPANT,
                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_EMAIL
                ) && (
                  <p className='text-xs sm:text-sm text-gray-600 truncate mt-0.5'>
                    {participantInfo.email}
                  </p>
                )}
              </div>
            </div>
            
            {/* Right Section - Action Buttons */}
            <div
              className='flex flex-wrap items-center gap-2 sm:gap-3 text-sm print:hidden'
              ref={buttonsContainerRef}
            >
              {loadHeaderButton()}
              {casePermissionHandle() && (
                <CustomButton
                  label='Case Notes'
                  clickHandler={handleAddCaseNote}
                  Icon={<BiPlus />}
                  variant='secondary'
                  cssClass='hidden sm:flex'
                />
              )}
              <NotificationDropDown />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ParticipantProfileHeader;
